import { useMemo } from 'react';

import { CopilotMessage, useCopilot } from '../../CopilotContext';
import { MessageArtifacts } from '../MessageArtifacts/MessageArtifacts';

import { renderMarkdown } from './renderMarkdown';
import styles from './Message.module.css';

interface Props {
  message: CopilotMessage;
}

// Friendly, present-tense labels for tool-call chips — never surface raw tool
// names like "get_worklist" to the doctor.
const TOOL_LABELS: Record<string, string> = {
  get_worklist: 'Fetching worklist…',
  search_studies: 'Searching studies…',
  get_study_context: 'Reading the study…',
  get_study_metadata: 'Reading the study…',
  get_measurements: 'Reading measurements…',
  explain_measurement: 'Checking the reference range…',
  get_reference_range: 'Looking up reference range…',
  get_patient_priors: 'Finding prior studies…',
  compare_measurements: 'Comparing studies…',
  compare_to_prior: 'Comparing to the prior study…',
  get_measurement_trend: 'Analyzing the trend…',
  draft_impression: 'Drafting the impression…',
  report_quality_check: 'Checking the report…',
  insert_report_draft: 'Preparing a draft…',
  set_report_status: 'Updating report status…',
  navigate_to_study: 'Opening the study…',
  navigate_to_dicom: 'Opening the image…',
  navigate_to_settings: 'Opening settings…',
  open_measurement: 'Opening the measurement…',
  highlight_measurement: 'Highlighting…',
  assign_study_to_me: 'Assigning the study…',
  complete_assignment: 'Completing the assignment…',
  list_dicoms: 'Reading images…',
  list_ai_scans: 'Reading AI analyses…',
  get_dicom_details: 'Reading the image…',
  get_scan_status: 'Checking analysis status…',
  get_report: 'Reading the report…',
  draft_report_section: 'Drafting the report…',
};

function toolLabel(name: string): string {
  return TOOL_LABELS[name] ?? 'Working…';
}

export function Message({ message }: Props) {
  const { retryLastTurn } = useCopilot();
  const isUser = message.role === 'user';

  // While the assistant turn is still streaming, suppress the partial text:
  // the typing dots + tool chips convey progress, and the full message is
  // revealed once (on "done"). No letter-by-letter.
  const streaming = !isUser && message.pending;
  const showText = !streaming && !!message.text;
  const hasChips = message.toolCalls.length > 0;
  const showArtifacts =
    !isUser && !streaming && (message.artifacts?.length ?? 0) > 0;
  const showBubble = isUser || showText || message.interrupted;

  // Assistant text is rendered as markdown; user bubbles stay plain text so
  // their own typed "*" or "_" don't get interpreted as emphasis.
  const html = useMemo(
    () => (showText && !isUser ? renderMarkdown(message.text) : ''),
    [showText, isUser, message.text]
  );

  // Nothing to draw yet (pre-text / pre-chip streaming) — the dots cover it.
  if (!isUser && !showBubble && !(streaming && hasChips) && !showArtifacts) {
    return null;
  }

  const renderBody = () => {
    if (isUser) return message.text;
    if (showText) {
      return (
        <div
          className={styles.markdown}
          // Escaped + formatted in renderMarkdown (no raw HTML passthrough).
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: html }}
        />
      );
    }
    if (message.interrupted) return 'Response was cut off.';
    return '';
  };

  return (
    <div className={`${styles.row} ${isUser ? styles.user : styles.assistant}`}>
      {showBubble && (
        <div
          className={`${styles.bubble} ${
            isUser ? styles.userBubble : styles.assistantBubble
          }`}
        >
          {renderBody()}
        </div>
      )}
      {streaming && hasChips && (
        <div className={styles.toolCalls}>
          {message.toolCalls.map((tc) => (
            <span
              key={tc.id}
              className={styles.toolChip}
              data-status={tc.status}
            >
              <span className={styles.chipSpinner} aria-hidden="true" />
              {toolLabel(tc.name)}
            </span>
          ))}
        </div>
      )}
      {showArtifacts && (
        <MessageArtifacts artifacts={message.artifacts ?? []} />
      )}
      {message.interrupted && !isUser && (
        <button
          type="button"
          className={styles.retryBtn}
          onClick={retryLastTurn}
        >
          Retry last turn
        </button>
      )}
    </div>
  );
}
